import {
  useState,
  forwardRef,
  type ReactNode,
  type Ref,
  type CSSProperties,
  type ButtonHTMLAttributes,
  type AnchorHTMLAttributes,
} from 'react';
import { useTheme } from '@vitness/theme';
import { density as densityTokens } from '@vitness/tokens';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost';
export type ButtonSize = 'default' | 'sm' | 'xs';

interface ButtonOwnProps {
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: ReactNode;
  children?: ReactNode;
}

/** Default form — renders a native `<button>`. */
export type ButtonAsButtonProps = ButtonOwnProps &
  Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> & { href?: undefined };

/** Link form — renders an `<a>` with identical styling when `href` is provided. */
export type ButtonAsLinkProps = ButtonOwnProps &
  Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'children'> & { href: string };

export type ButtonProps = ButtonAsButtonProps | ButtonAsLinkProps;

/**
 * Themed button. Renders a `<button>` by default, or an `<a>` when `href` is
 * supplied — so navigating CTAs stay real links (open-in-new-tab, SEO) while
 * sharing one styled, token-driven surface. Styling is identical across both.
 */
export const Button = forwardRef<HTMLButtonElement | HTMLAnchorElement, ButtonProps>(function Button(
  props,
  ref,
) {
  const { variant = 'primary', size = 'default', icon, children, style, ...rest } = props;
  const { accent } = useTheme();
  const { density } = useTheme();
  const d = densityTokens[density];
  const [hov, setHov] = useState(false);

  const isLink = props.href !== undefined;
  const isDisabled = !isLink && Boolean((props as ButtonAsButtonProps).disabled);

  const pad = size === 'sm' ? d.buttonSmPadding : size === 'xs' ? d.buttonXsPadding : d.buttonPadding;
  const fs = size === 'sm' ? d.buttonSmFontSize : size === 'xs' ? d.buttonXsFontSize : d.buttonFontSize;

  const base: CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
    padding: pad,
    fontFamily: 'var(--vn-font-body)',
    fontSize: fs,
    fontWeight: 600,
    borderRadius: 6,
    cursor: isDisabled ? 'not-allowed' : 'pointer',
    border: 'none',
    transition: 'background 0.15s, border-color 0.15s, opacity 0.15s',
    opacity: isDisabled ? 0.45 : hov ? 0.88 : 1,
    textDecoration: 'none',
    ...style,
  };

  if (variant === 'primary') {
    Object.assign(base, { background: accent, color: '#000' });
  } else if (variant === 'secondary') {
    Object.assign(base, {
      background: hov ? 'var(--vn-surface-card-hover)' : 'transparent',
      border: '1px solid var(--vn-border-strong)',
      color: 'var(--vn-text-primary)',
    });
  } else {
    Object.assign(base, {
      background: 'transparent',
      color: hov ? 'var(--vn-text-primary)' : 'var(--vn-text-secondary)',
    });
  }

  const onMouseEnter = () => setHov(true);
  const onMouseLeave = () => setHov(false);

  if (isLink) {
    return (
      <a
        ref={ref as Ref<HTMLAnchorElement>}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        style={base}
        {...(rest as AnchorHTMLAttributes<HTMLAnchorElement>)}
      >
        {children}
        {icon}
      </a>
    );
  }

  return (
    <button
      ref={ref as Ref<HTMLButtonElement>}
      type="button"
      disabled={isDisabled}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      style={base}
      {...(rest as ButtonHTMLAttributes<HTMLButtonElement>)}
    >
      {children}
      {icon}
    </button>
  );
});
